import React, { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Users, Send, Search, ArrowRight, X, Loader2, CheckCircle2, Copy } from "lucide-react";
import AppShell from "../components/app/AppShell";
import PageHeader, { StatusPill, EmptyState } from "../components/app/PageHeader";
import api, { formatApiError } from "../lib/api";
import { track, EVT } from "../lib/analytics";

function SendInvitationModal({ open, onClose, onSent }) {
  const [step, setStep] = useState(1);
  const [flows, setFlows] = useState([]);
  const [flowId, setFlowId] = useState("");
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", reference: "", title: "", notes: "" });
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (open) {
      api.get("/api/flows?status=active").then(({ data }) => setFlows(data)).catch(() => {});
    } else {
      setStep(1); setFlowId(""); setSuccess(null);
    }
  }, [open]);

  if (!open) return null;

  const submit = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/api/clients/send", { flow_id: flowId, ...form, email_subject: emailSubject || null, email_body: emailBody || null });
      track(EVT.client_invited, { flow_id: flowId });
      setSuccess(data); onSent && onSent(data);
    } catch (e) { alert(formatApiError(e)); }
    finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" data-testid="send-invitation-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative max-w-xl mx-auto my-10 card2 p-6 animate-slide-up">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="t-h2">Send onboarding</h3>
            <p className="t-caption">Step {success ? "✓" : step} of 4</p>
          </div>
          <button onClick={onClose} className="p-2 text-slate2 hover:text-ink"><X size={20} /></button>
        </div>

        {success ? (
          <div className="text-center py-6">
            <CheckCircle2 size={48} className="text-emerald2 mx-auto mb-4" />
            <h4 className="t-h3 mb-2">Invitation sent to {success.email_masked}</h4>
            <p className="t-body-sm text-slate2 mb-4">Your client will receive an email shortly.</p>
            {success.dev_portal_link && (
              <div className="text-left p-3 mb-4 rounded-md t-mono t-caption" style={{ background: "#FEF3C7", color: "#92400E", border: "1px solid #FDE68A" }}>
                <strong>Dev portal link:</strong><br /><span className="break-all">{success.dev_portal_link}</span>
              </div>
            )}
            <Link to={`/app/clients/${success.id}`} className="btn btn-primary">View client <ArrowRight size={14} /></Link>
          </div>
        ) : (
          <>
            {step === 1 && (
              <>
                <label className="label2">Which active flow should this client complete?<span className="req">*</span></label>
                <select className="input2 mb-4" value={flowId} onChange={(e) => setFlowId(e.target.value)} data-testid="send-flow-select">
                  <option value="">— select an active flow —</option>
                  {flows.map((f) => <option key={f.id} value={f.id}>{f.name} ({f.step_count} steps)</option>)}
                </select>
                {flows.length === 0 && <p className="t-caption mb-3">You need at least one active flow. <Link to="/app/flows" className="text-indigo2 hover:underline">Create one →</Link></p>}
                <div className="flex justify-end gap-2"><button className="btn btn-ghost" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={() => setStep(2)} disabled={!flowId}>Next</button></div>
              </>
            )}
            {step === 2 && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="label2">First name<span className="req">*</span></label><input className="input2" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="client-first" /></div>
                  <div><label className="label2">Last name<span className="req">*</span></label><input className="input2" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="client-last" /></div>
                  <div className="col-span-2"><label className="label2">Email<span className="req">*</span></label><input type="email" className="input2" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="client-email" /></div>
                  <div><label className="label2">Title</label>
                    <select className="input2" value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })}>
                      <option value="">—</option><option>Mr</option><option>Mrs</option><option>Ms</option><option>Miss</option><option>Dr</option><option>Prof</option>
                    </select>
                  </div>
                  <div><label className="label2">Phone (optional)</label><input type="tel" className="input2" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                  <div className="col-span-2"><label className="label2">Reference / case # (optional)</label><input className="input2" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} /></div>
                  <div className="col-span-2"><label className="label2">Internal notes (not shown to client)</label><textarea className="textarea2" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
                </div>
                <div className="mt-5 flex justify-between"><button className="btn btn-ghost" onClick={() => setStep(1)}>Back</button><button className="btn btn-primary" onClick={() => setStep(3)} disabled={!form.first_name || !form.last_name || !form.email}>Next</button></div>
              </>
            )}
            {step === 3 && (
              <>
                <label className="label2">Email subject</label>
                <input className="input2 mb-3" placeholder={`${flows.find((f) => f.id === flowId)?.name} — onboarding invitation`} value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} />
                <label className="label2">Custom message (optional)</label>
                <textarea className="textarea2 mb-3" rows={5} placeholder="Leave blank for our default branded invitation." value={emailBody} onChange={(e) => setEmailBody(e.target.value)} />
                <div className="mt-3 flex justify-between"><button className="btn btn-ghost" onClick={() => setStep(2)}>Back</button><button className="btn btn-primary" onClick={() => setStep(4)}>Review</button></div>
              </>
            )}
            {step === 4 && (
              <>
                <div className="space-y-3 mb-5">
                  <div className="p-4 rounded-md bg-surface"><div className="t-label text-slate2 mb-1">Flow</div><div className="t-body">{flows.find((f) => f.id === flowId)?.name}</div></div>
                  <div className="p-4 rounded-md bg-surface"><div className="t-label text-slate2 mb-1">Client</div><div className="t-body">{form.first_name} {form.last_name} · {form.email}</div></div>
                </div>
                <div className="flex justify-between"><button className="btn btn-ghost" onClick={() => setStep(3)}>Back</button><button className="btn btn-primary" onClick={submit} disabled={sending} data-testid="confirm-send">{sending ? <><Loader2 size={14} className="animate-spin" />Sending…</> : <>Send invitation <Send size={14} /></>}</button></div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default function Clients() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [sendOpen, setSendOpen] = useState(searchParams.get("send") === "1");

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (status !== "all") p.set("status", status);
      if (q) p.set("q", q);
      const { data } = await api.get(`/api/clients?${p}`);
      setItems(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [status]);

  return (
    <AppShell>
      <PageHeader
        title="Active clients"
        subtitle="Track every client's onboarding journey from invite to completion."
        actions={<button className="btn btn-primary" onClick={() => setSendOpen(true)} data-testid="send-new-invitation"><Send size={16} />Send onboarding</button>}
      />

      <div className="card2 p-4 mb-5 flex flex-col md:flex-row gap-3 items-stretch md:items-center">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate2" />
          <input className="input2 pl-9" placeholder="Search by name or email…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} />
        </div>
        <select className="input2 md:w-48" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="all">All statuses</option>
          <option value="invited">Invited</option>
          <option value="started">Started</option>
          <option value="in_progress">In progress</option>
          <option value="awaiting_review">Awaiting review</option>
          <option value="complete">Complete</option>
          <option value="stalled">Stalled</option>
        </select>
      </div>

      {loading ? (
        <div className="space-y-2">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="skeleton h-16" />)}</div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={Users}
          title="No clients yet"
          description="Send your first onboarding invitation to get started."
          action={<button className="btn btn-primary" onClick={() => setSendOpen(true)}>Send onboarding</button>}
        />
      ) : (
        <div className="card2 overflow-hidden">
          <table className="w-full" data-testid="clients-table">
            <thead className="bg-surface border-b border-border2">
              <tr className="text-left">
                <th className="px-5 py-3 t-label text-slate2">Client</th>
                <th className="px-5 py-3 t-label text-slate2">Flow</th>
                <th className="px-5 py-3 t-label text-slate2">Sent by</th>
                <th className="px-5 py-3 t-label text-slate2">Status</th>
                <th className="px-5 py-3 t-label text-slate2">Last activity</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border2">
              {items.map((c) => (
                <tr key={c.id} className="hover:bg-surface" data-testid={`client-row-${c.id}`}>
                  <td className="px-5 py-4">
                    <Link to={`/app/clients/${c.id}`} className="t-body font-medium text-ink hover:text-indigo2">{c.first_name} {c.last_name}</Link>
                    <div className="t-caption">{c.email_masked}</div>
                  </td>
                  <td className="px-5 py-4 t-body-sm">{c.flow_name}</td>
                  <td className="px-5 py-4 t-caption">{c.sent_by_name}</td>
                  <td className="px-5 py-4"><StatusPill status={c.status} /></td>
                  <td className="px-5 py-4 t-caption">{c.last_activity_at ? new Date(c.last_activity_at).toLocaleString() : "—"}</td>
                  <td className="px-5 py-4"><Link to={`/app/clients/${c.id}`} className="btn btn-ghost btn-sm">View <ArrowRight size={14} /></Link></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <SendInvitationModal open={sendOpen} onClose={() => { setSendOpen(false); setSearchParams({}); load(); }} onSent={() => load()} />
    </AppShell>
  );
}
